import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageSquare, Send, Paperclip, Image, FileText, Video, User, X, Loader2, Trash2, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = '/api';

const ChatPage = () => {
  const { isSuperAdmin, user, token } = useAuth();

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API_URL}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Mensagem excluida');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir mensagem');
    }
  };

  const [messages, setMessages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectsAPI.list();
      setProjects(response.data);
      if (response.data.length > 0) setSelectedProject(user?.project_id || response.data[0].id);
    } catch (error) { console.error(error); }
  }, [user?.project_id]);

  const fetchMessages = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const response = await messagesAPI.list(selectedProject);
      setMessages(response.data);
      setTimeout(scrollToBottom, 100);
    } catch (error) { toast.error('Erro ao carregar mensagens'); }
    finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}`;
    const ws = new WebSocket(`${wsUrl}/ws/chat/${selectedProject}`);
    ws.onmessage = (event) => {
      const newMsg = JSON.parse(event.data);
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
      setTimeout(scrollToBottom, 100);
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [selectedProject]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { if (selectedProject) { setLoading(true); fetchMessages(); } }, [selectedProject, fetchMessages]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Arquivo muito grande. Maximo 10MB'); return; }
    setPendingFile(file);
  };

  const removePendingFile = () => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !pendingFile) || !selectedProject) return;
    setSending(true);
    try {
      let fileData = null;
      if (pendingFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', pendingFile);
        const res = await axios.post(`${API_URL}/upload`, formData, { headers: { Authorization: `Bearer ${token}` } });
        fileData = res.data;
        setUploading(false);
      }
      const messagePayload = { project_id: selectedProject, content: newMessage.trim() || (fileData ? fileData.file_name : ''), file_url: fileData?.file_url || null, file_type: fileData?.file_type || null };
      const response = await messagesAPI.create(messagePayload);
      setMessages(prev => {
        if (prev.some(m => m.id === response.data.id)) return prev;
        return [...prev, response.data];
      });
      setTimeout(scrollToBottom, 100);
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(response.data));
      setNewMessage(''); setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) { toast.error('Erro ao enviar mensagem'); setUploading(false); }
    finally { setSending(false); }
  };

  const formatTime = (ts) => new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const formatDate = (ts) => {
    const d = new Date(ts), t = new Date(), y = new Date(t); y.setDate(y.getDate() - 1);
    if (d.toDateString() === t.toDateString()) return 'Hoje';
    if (d.toDateString() === y.toDateString()) return 'Ontem';
    return d.toLocaleDateString('pt-BR');
  };

  const groupMessagesByDate = (msgs) => {
    const g = {};
    msgs.forEach(m => { const d = formatDate(m.created_at); if (!g[d]) g[d] = []; g[d].push(m); });
    return g;
  };

  const isImageFile = (ft) => ft?.startsWith('image');
  const getFileIcon = (ft) => ft?.startsWith('image') ? Image : ft?.startsWith('video') ? Video : FileText;
  const groupedMessages = groupMessagesByDate(messages);

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="chat-page">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <MessageSquare size={20} className="text-gold" /> CHAT DO PROJETO
        </h2>
        {(isSuperAdmin || projects.length > 1) && (
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[250px] input-hud" data-testid="chat-project-select"><SelectValue placeholder="Selecione o projeto" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <div className="flex-1 card-hud overflow-hidden flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center"><div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" /></div>
        ) : selectedProject ? (
          <ScrollArea className="flex-1 p-4">
            {Object.keys(groupedMessages).length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <MessageSquare size={48} className="mb-4 text-gray-600" />
                <p className="font-mono">Nenhuma mensagem ainda</p>
              </div>
            ) : (
              Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date}>
                  <div className="flex items-center gap-4 my-4">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="text-gray-400 text-xs font-mono">{date}</span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>
                  {msgs.map((message) => {
                    const isOwn = message.user_id === user?.id || message.user_id === 'super_admin';
                    const FileIcon = message.file_type ? getFileIcon(message.file_type) : null;
                    return (
                      <div key={message.id} className={`flex mb-4 animate-fade-in ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`message-${message.id}`}>
                        <div className={`max-w-[70%]`}>
                          {!isOwn && (
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center"><User size={12} className="text-blue-600" /></div>
                              <span className="text-blue-600 text-xs font-mono">{message.user_name}</span>
                            </div>
                          )}
                          <div className={`p-3 rounded-xl ${isOwn ? 'bg-gold/10 border border-gold/20' : 'bg-gray-100 border border-gray-200'} group relative`}>
                            {(isOwn || isSuperAdmin) && (
                              <button
                                onClick={() => deleteMessage(message.id)}
                                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-sm"
                                data-testid={`delete-msg-${message.id}`}
                                title="Excluir mensagem"
                              >
                                <Trash2 size={11} />
                              </button>
                            )}
                            {message.content && <p className="text-navy text-sm whitespace-pre-wrap break-words">{message.content}</p>}
                            {message.file_url && (
                              <div className="mt-2">
                                {isImageFile(message.file_type) ? (
                                  <div>
                                    <a href={`${message.file_url}`} target="_blank" rel="noopener noreferrer" className="block">
                                      <img src={`${message.file_url}`} alt="Anexo" className="max-w-full max-h-48 rounded-lg object-cover" />
                                    </a>
                                    <a href={`${message.file_url}?download=1`} className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-600 hover:text-navy transition-colors" data-testid={`download-file-${message.id}`}>
                                      <Download size={12} /> Baixar imagem
                                    </a>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                                    {FileIcon && <FileIcon size={18} className="text-gold flex-shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-navy text-sm font-medium truncate">{message.content || 'Arquivo'}</p>
                                      <p className="text-gray-400 text-[10px] font-mono">{message.file_type}</p>
                                    </div>
                                    <a href={`${message.file_url}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 transition-colors p-1" title="Abrir" data-testid={`open-file-${message.id}`}>
                                      <ExternalLink size={14} />
                                    </a>
                                    <a href={`${message.file_url}?download=1`} className="text-gray-400 hover:text-gold transition-colors p-1" title="Baixar" data-testid={`download-file-${message.id}`}>
                                      <Download size={14} />
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                            <span className={`text-[10px] font-mono mt-2 block ${isOwn ? 'text-gold/60 text-right' : 'text-gray-400'}`}>{formatTime(message.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </ScrollArea>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400"><p className="font-mono">Selecione um projeto</p></div>
        )}

        {pendingFile && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 text-sm">
              <Paperclip size={14} className="text-gold" />
              <span className="text-gray-600 truncate flex-1">{pendingFile.name}</span>
              <span className="text-gray-400 text-xs">{(pendingFile.size / 1024).toFixed(0)} KB</span>
              <button onClick={removePendingFile} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          </div>
        )}

        {selectedProject && (
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" data-testid="chat-file-input" />
              <Button type="button" variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} className="text-gray-400 hover:text-gold hover:bg-gold/10 h-10 w-10 shrink-0" disabled={sending || uploading} data-testid="chat-upload-btn">
                <Paperclip size={18} />
              </Button>
              <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} className="input-hud flex-1" placeholder={pendingFile ? "Adicione uma mensagem..." : "Digite sua mensagem..."} disabled={sending || uploading} data-testid="chat-input" />
              <Button type="submit" className="btn-primary" disabled={(!newMessage.trim() && !pendingFile) || sending || uploading} data-testid="send-message-btn">
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
